import { z } from 'zod';
export const verdictSchema = z.enum(['PASS', 'FAIL', 'NEEDS_IN_GAME', 'N_A']);
/**
 * Accepts live verdicts plus legacy AI/tester values that were removed from
 * the picker. Folded into `NEEDS_IN_GAME` so old on-disk reviews still load.
 */
export const verdictParseSchema = z.union([
    verdictSchema,
    z.literal('INCONCLUSIVE').transform(() => 'NEEDS_IN_GAME'),
    z.literal('NEEDS_CLARIFICATION').transform(() => 'NEEDS_IN_GAME'),
]);
export const VERDICT_LABEL = {
    PASS: 'Pass',
    FAIL: 'Fail',
    NEEDS_IN_GAME: 'Needs in-game verification',
    N_A: 'N/A',
};
export const VERDICT_CSS = {
    PASS: 'pass',
    FAIL: 'fail',
    NEEDS_IN_GAME: 'in-game',
    N_A: 'na',
};
export const xrayStatusSchema = z.enum(['PASSED', 'FAIL', 'N/A', 'BLOCKED', 'TO DO']);
export const phaseSchema = z.enum([
    'pull-source',
    'drive',
    'analyze-images',
    'load-tests',
    'analyze-tests',
]);
export const phaseStatusSchema = z.enum([
    'pending',
    'in_progress',
    'completed',
    'failed',
    'skipped',
]);
/**
 * Per-file findings produced by the source-code "scout" pass in the two-tier
 * review pipeline. The scout reads ONE entire source file with a long-context,
 * code-strong model and extracts the line ranges the aggregator (flagship)
 * model should consider when issuing the final verdict.
 *
 * Hard contract: the scout is an evidence extractor, not a judge. Verdict /
 * pass / fail / severity / bug fields are explicitly forbidden on this shape
 * via `.strict()` plus a refinement, so even if the scout model misbehaves
 * the verdict-shaped output never reaches the aggregator.
 */
const SCOUT_FORBIDDEN_KEY = /verdict|pass|fail|severity|bug|confidence/i;
export const fileScoutExcerptSchema = z
    .object({
    /** Inclusive 1-based start/end line range in the source file. */
    lines: z.tuple([z.number().int().positive(), z.number().int().positive()]),
    /** Verbatim source-code slice (never paraphrased / summarised). */
    content: z.string(),
    /** One- or two-sentence descriptive justification. Must NOT issue a verdict. */
    reasoning: z.string(),
})
    .passthrough();
const SCOUT_KNOWN_KEYS = new Set([
    'file',
    'relative_path',
    'sha1',
    'is_relevant',
    'summary',
    'excerpts',
    'open_questions',
    'scout_error',
]);
export const fileScoutFindingsSchema = z
    .object({
    /** Display filename (e.g. `FruityDiamondStacks_93.js`). */
    file: z.string(),
    /** Path relative to the extracted project folder. */
    relative_path: z.string(),
    /** SHA-1 of the source file at scout time; doubles as the cache key. */
    sha1: z.string(),
    /**
     * Whether this file contains anything potentially relevant to the test.
     * False ONLY when literally nothing in the file relates - the aggregator
     * is free to disagree.
     */
    is_relevant: z.boolean(),
    /**
     * Descriptive one-line gist of what the file contains for THIS test.
     * Must NOT predict a verdict (e.g. "declares maxWin at line 4805" is
     * good; "the test fails because maxWin is 5000" is forbidden).
     */
    summary: z.string(),
    excerpts: z.array(fileScoutExcerptSchema).default([]),
    /**
     * Things the scout could partially answer but that need cross-checking
     * against other files / screenshots / video. The aggregator decides.
     */
    open_questions: z.array(z.string()).default([]),
    /**
     * When the scout call itself failed (model 400, parse error, context
     * overflow), this carries the human-readable reason. `is_relevant`
     * defaults to true so the aggregator is alerted rather than silently
     * dropping the file.
     */
    scout_error: z.string().nullable().optional(),
})
    .passthrough()
    .superRefine((val, ctx) => {
    // Reject any verdict-shaped extra key the scout model might have leaked
    // in despite the prompt forbidding it. We keep `.passthrough()` so a
    // harmless extra ("notes", "version") doesn't fail parsing - only
    // verdict-like keys are blocked.
    for (const key of Object.keys(val)) {
        if (SCOUT_KNOWN_KEYS.has(key))
            continue;
        if (SCOUT_FORBIDDEN_KEY.test(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [key],
                message: `scout output must not contain verdict-shaped fields (forbidden: ${key})`,
            });
        }
    }
});
export const aiAssessmentSchema = z.object({
    verdict: verdictParseSchema,
    confidence: z.enum(['low', 'medium', 'high']),
    rationale: z.string(),
    steps_supported_by_code: z.array(z.string()).default([]),
    steps_needing_in_game: z.array(z.string()).default([]),
    evidence: z
        .object({
        code: z
            .array(z.object({
            file: z.string(),
            lines: z.tuple([z.number(), z.number()]).optional(),
            excerpt: z.string(),
        }))
            .default([]),
        images: z
            .array(z.object({
            name: z.string(),
            relevant_text: z.string(),
            // Optional video-frame coordinates. When present, this evidence
            // entry refers to one or more seconds of a gameplay video
            // instead of a Phase 4 game-rules screenshot. The frontend
            // uses these to build a `/videos/:slug/frames/frame_NNNNN.jpg`
            // URL so the thumbnail still renders even though `name` is a
            // label rather than a real screenshot filename.
            //
            // `second` is the start of the citation. `second_end` (when
            // present) is the INCLUSIVE end of the cited range; the
            // frontend uses it to open a range modal showing every frame
            // between `second` and `second_end`. Omit `second_end` for a
            // point citation.
            video_slug: z.string().optional(),
            second: z.number().int().nonnegative().optional(),
            second_end: z.number().int().nonnegative().optional(),
        }))
            .default([]),
    })
        .default({ code: [], images: [] }),
    /**
     * Per-category narrative the AI writes to explain WHY each kind of
     * evidence supports (or contradicts) the verdict. Categories are
     * OPTIONAL: a purely visual check (e.g. "is a clock available") leaves
     * `source_code` empty rather than inventing code; a maths/RTP check may
     * leave `game_rules_images` / `videos` empty. The UI renders only the
     * sections that have content. `videos` carries one entry per relevant
     * gameplay video (keyed by slug) so the reasoning is grouped per clip;
     * the actual frame thumbnails still come from `evidence.images`.
     */
    evidence_summary: z
        .object({
        source_code: z.string().default(''),
        game_rules_images: z.string().default(''),
        videos: z
            .array(z.object({
            slug: z.string(),
            reasoning: z.string(),
        }))
            .default([]),
    })
        .default({ source_code: '', game_rules_images: '', videos: [] }),
    suggested_bug: z
        .object({
        summary: z.string(),
        description_md: z.string(),
        severity: z.enum(['minor', 'major', 'blocker']),
    })
        .optional(),
    /**
     * Per-file findings produced by the source-code scout pass that fed this
     * verdict. Attached server-side AFTER the flagship aggregator returns, so
     * the UI can show what each scout extracted. The flagship never emits this
     * field itself.
     */
    scout_findings: z.array(fileScoutFindingsSchema).optional(),
    /**
     * How much game/math context was injected for this verdict (`none` = unchanged
     * pipeline). Attached server-side after the aggregator returns.
     */
    game_math_relevance: z.enum(['none', 'context', 'investigate']).optional(),
    /**
     * Extra findings from the game/math investigator tool loop (investigate-level
     * tests only). Attached server-side; the flagship never emits this field.
     */
    game_math_findings: z.string().optional(),
});
export const runSchema = z.object({
    id: z.string(),
    sr_key: z.string(),
    texec_key: z.string(),
    studio: z.string(),
    game_name: z.string(),
    status: z.string(),
    project_path: z.string().nullable(),
    images_path: z.string().nullable(),
    created_at: z.string(),
    phases: z.record(phaseSchema, phaseStatusSchema).default({}),
    owner_email: z.string().nullable().optional(),
    assignee: z.string().nullable().optional(),
    closed_at: z.string().nullable().optional(),
    provenance: z
        .object({
        gitlab_project_id: z.string().nullable().optional(),
        gitlab_branch: z.string().nullable().optional(),
        gitlab_commit_sha: z.string().nullable().optional(),
        archive_path: z.string().nullable().optional(),
        archive_sha256: z.string().nullable().optional(),
        archive_bytes: z.number().nullable().optional(),
        pulled_at: z.string().nullable().optional(),
        drive_images_link: z.string().nullable().optional(),
        drive_video_links: z.array(z.string()).optional(),
    })
        .optional(),
    artifacts: z
        .object({
        files: z.enum(['available', 'pruned', 'missing']).optional(),
        videos: z.enum(['available', 'pruned', 'missing']).optional(),
        images: z.enum(['available', 'pruned', 'missing']).optional(),
        frames: z.enum(['available', 'pruned', 'missing']).optional(),
    })
        .optional(),
});
export const relatedRequirementSchema = z.object({
    key: z.string(),
    summary: z.string(),
    jurisdictions: z.array(z.string()).default([]),
    status: z.string().nullable(),
    description: z.string().nullable().default(null),
    labels: z.array(z.string()).default([]),
});
export const testReviewSchema = z.object({
    test_key: z.string(),
    test_summary: z.string(),
    test_description: z.string().nullable(),
    test_steps: z
        .array(z.object({
        action: z.string().nullable().optional(),
        data: z.string().nullable().optional(),
        result: z.string().nullable().optional(),
    }))
        .nullable(),
    test_labels: z.array(z.string()).default([]),
    xray_status: xrayStatusSchema.nullable(),
    related_requirements: z.array(relatedRequirementSchema).default([]),
    ai_verdict: verdictParseSchema.nullable(),
    ai_confidence: z.enum(['low', 'medium', 'high']).nullable(),
    ai_assessment: aiAssessmentSchema.nullable(),
    tester_verdict: verdictParseSchema.nullable(),
    tester_justification: z.string().nullable(),
    tester_notes: z.string().nullable(),
    /**
     * Legacy single-bug pointer kept on the schema for compat with reviews
     * persisted before the multi-bug migration. New code reads / writes via
     * `bug_keys`; the repo synchronises both fields so older clients keep
     * showing the most-recent bug as a chip.
     */
    bug_key: z.string().nullable(),
    /** Every Jira bug raised from this test, in creation order. */
    bug_keys: z.array(z.string()).default([]),
    /**
     * Result of playing this test live via Look at that. Separate from
     * `ai_assessment` (document review) — never overwrites it and never
     * auto-finalizes; the tester still owns the Xray verdict.
     */
    in_game_play: z
        .object({
        verdict: verdictSchema,
        rationale: z.string(),
        playedAt: z.string(),
        shotFiles: z.array(z.string()),
    })
        .nullable()
        .optional(),
    finalized_at: z.string().nullable(),
    updated_at: z.string(),
});
export const chatMessageSchema = z.object({
    id: z.number(),
    role: z.enum(['user', 'assistant']),
    content: z.string(),
    ts: z.string(),
});
// Game Knowledge Base (Extras) ----------------------------------------------
//
// A per-run knowledge base for a single game: a short Game Description, a
// detailed Full Report, and a dedicated chat grounded in those artifacts +
// all the run evidence (source package, image OCR, video analysis). Both
// reports are stored as Markdown on disk; this metadata tracks their
// generation state so the UI can show "running / ready / error" without
// re-reading the files.
export const gameKbArtifactStatusSchema = z.enum(['idle', 'running', 'ready', 'error']);
export const gameKbArtifactMetaSchema = z.object({
    status: gameKbArtifactStatusSchema.default('idle'),
    model: z.string().nullable().default(null),
    updatedAt: z.string().nullable().default(null),
    error: z.string().nullable().default(null),
});
export const gameKbMetaSchema = z.object({
    description: gameKbArtifactMetaSchema.default({ status: 'idle', model: null, updatedAt: null, error: null }),
    report: gameKbArtifactMetaSchema.default({ status: 'idle', model: null, updatedAt: null, error: null }),
});
export const gameKbArtifactsSchema = z.object({
    description: z.string().nullable().default(null),
    report: z.string().nullable().default(null),
    meta: gameKbMetaSchema,
});
/** The three places game-kb progress can originate from. */
export const gameKbStageSchema = z.enum(['description', 'report', 'chat']);
// Stake Engine verification (Extras, EAS only) ------------------------------
//
// For EasyGo Games (EAS) the game is a Stake Engine submission: pre-computed
// "books" + weighted lookup tables + a provably-fair RNG. This block models
// the independent verifier (ported from the Python Staker tool): three
// deterministic gates run against the GitLab-pulled package, plus a dedicated
// chat about the results. The whole report is stored as JSON on disk.
/** Volatility classification derived from the per-bet payout std. */
export const stakeVolatilityClassSchema = z.enum(['LOW', 'MEDIUM', 'HIGH']);
/**
 * One payout-multiplier bucket of the per-mode outcome distribution.
 * `hi` is null for the open-ended top bucket. `effectiveHitRate` is the
 * "1 in N" odds of landing in this bucket (null when the bucket is empty),
 * and `rtpContributionPct` is this bucket's share of the mode RTP% (the
 * contributions across all buckets sum to the mode rtpPct).
 */
export const stakeDistBucketSchema = z.object({
    label: z.string(),
    lo: z.number(),
    hi: z.number().nullable().default(null),
    distinctCount: z.number().int().default(0),
    effectiveHitRate: z.number().nullable().default(null),
    rtpContributionPct: z.number().default(0),
});
/** Per-mode analytic math statistics (exact, from the weighted lookup table). */
export const stakeModeStatsSchema = z.object({
    mode: z.string(),
    cost: z.number(),
    rows: z.number().int(),
    /** Sum of all weights as a decimal string (can exceed Number.MAX_SAFE_INTEGER). */
    totalWeight: z.string(),
    rtpPct: z.number(),
    maxMultiplier: z.number(),
    /** 1 / P(payout > 0); null when the mode never wins. */
    hitRate: z.number().nullable(),
    std: z.number(),
    probZero: z.number(),
    /** Whether rtpPct is within 0.5pp of the declared RTP; null when none declared. */
    reconciles: z.boolean().nullable(),
    notes: z.array(z.string()).default([]),
    // --- Extended distribution stats (all exact, from the lookup) -------------
    /** Mean payout multiplier per bet unit ( = rtpPct/100 * cost ). */
    mean: z.number().default(0),
    /** Smallest payout multiplier present (usually 0). */
    minMultiplier: z.number().default(0),
    /** P(payout > 0) as a percentage. */
    hitRatePct: z.number().default(0),
    /** P(payout < bet cost) - the "break-even" miss probability. */
    probLtBet: z.number().default(0),
    /** P(0 < payout < bet cost). */
    subBetRate: z.number().default(0),
    /** P(payout >= bet cost). */
    winRate: z.number().default(0),
    /** Number of distinct payout multipliers in the lookup. */
    uniquePayouts: z.number().int().default(0),
    /** "1 in N" odds of the single most-weighted simulation; null if unknown. */
    mostProbableHitRate: z.number().nullable().default(null),
    /** "1 in N" odds of hitting the max win; null if unknown. */
    maxWinHitRate: z.number().nullable().default(null),
    /** Average spins between profitable (payout >= bet) outcomes; null if never. */
    avgSpinsBetweenProfit: z.number().nullable().default(null),
    /** Worst-case consecutive zero-win streak at a 1-in-1000 chance. */
    worstZeroStreak: z.number().int().nullable().default(null),
    /** Worst-case consecutive losing (payout < bet) streak at a 1-in-1000 chance. */
    worstLossStreak: z.number().int().nullable().default(null),
    /** LOW / MEDIUM / HIGH classification from std; null if not computed. */
    volatilityClass: stakeVolatilityClassSchema.nullable().default(null),
    /** Payout-multiplier distribution histogram. */
    distribution: z.array(stakeDistBucketSchema).default([]),
});
/** Per-mode books <-> lookup integrity result. */
export const stakeIntegrityRowSchema = z.object({
    mode: z.string(),
    passed: z.boolean().nullable(),
    bookRecords: z.number().int().default(0),
    lookupRows: z.number().int().default(0),
    countMatch: z.boolean().default(false),
    payoutsMatch: z.boolean().default(false),
    bookSha256: z.string().default(''),
    lookupSha256: z.string().default(''),
    firstMismatch: z
        .object({ index: z.number().int(), book: z.number(), lookup: z.number() })
        .nullable()
        .default(null),
    error: z.string().nullable().default(null),
});
/**
 * One round-outcome class for a mode, taken from each book record's `criteria`
 * field (e.g. "basegame", "freegame", "wincap", "no win"). Shares are weighted
 * by the lookup weights so they are probability-correct; `rtpContributionPct`
 * values across classes sum to the mode RTP%.
 */
export const stakeRoundTypeSchema = z.object({
    label: z.string(),
    rounds: z.number().int().default(0),
    roundsPct: z.number().default(0),
    rtpContributionPct: z.number().default(0),
});
/** Raw occurrence count of one book event `type` across scanned rounds. */
export const stakeEventTypeSchema = z.object({
    type: z.string(),
    count: z.number().int().default(0),
});
/** Per-mode gameplay classification derived from the decompressed book events. */
export const stakeModeRoundStatsSchema = z.object({
    mode: z.string(),
    bookRecords: z.number().int().default(0),
    totalWeight: z.string().default('0'),
    /** Outcome breakdown by `criteria`, weighted by lookup weights. */
    byCriteria: z.array(stakeRoundTypeSchema).default([]),
    /** Most frequent event types present in this mode's books (top N). */
    eventTypes: z.array(stakeEventTypeSchema).default([]),
    /** Weighted % of rounds that entered a feature / free-spins. */
    featureRoundsPct: z.number().default(0),
    /** Weighted % of rounds that hit the win cap. */
    wincapRoundsPct: z.number().default(0),
    /** Distinct winning symbols seen across winInfo events. */
    distinctWinSymbols: z.number().int().default(0),
    /** Largest number of simultaneous wins in a single round. */
    maxWinsInRound: z.number().int().default(0),
    error: z.string().nullable().default(null),
    notes: z.array(z.string()).default([]),
});
/** Provably-fair reproduction summary against the vendor test vectors. */
export const stakeFairSummarySchema = z.object({
    total: z.number().int().default(0),
    randOk: z.number().int().default(0),
    rollOk: z.number().int().default(0),
    randFail: z.array(z.number().int()).default([]),
    rollFail: z.array(z.number().int()).default([]),
    serverSeed: z.string().nullable().default(null),
    clientSeed: z.string().nullable().default(null),
});
/** Shared envelope around each gate run (ran / passed / timing / error). */
const stakeGateBase = {
    ran: z.boolean().default(false),
    passed: z.boolean().nullable().default(null),
    error: z.string().nullable().default(null),
    seconds: z.number().default(0),
};
export const stakeMathGateSchema = z.object({
    ...stakeGateBase,
    declaredRtpPct: z.number().nullable().default(null),
    rows: z.array(stakeModeStatsSchema).default([]),
});
export const stakeIntegrityGateSchema = z.object({
    ...stakeGateBase,
    skipped: z.boolean().default(false),
    rows: z.array(stakeIntegrityRowSchema).default([]),
});
export const stakeFairGateSchema = z.object({
    ...stakeGateBase,
    summary: stakeFairSummarySchema.nullable().default(null),
});
export const stakeRoundGateSchema = z.object({
    ...stakeGateBase,
    skipped: z.boolean().default(false),
    rows: z.array(stakeModeRoundStatsSchema).default([]),
});
export const stakeReportStatusSchema = z.enum(['idle', 'running', 'ready', 'error']);
export const stakeReportSchema = z.object({
    status: stakeReportStatusSchema.default('idle'),
    /** True/false overall verdict, or null when nothing decisive ran. */
    overall: z.boolean().nullable().default(null),
    packageDir: z.string().nullable().default(null),
    mathDir: z.string().nullable().default(null),
    vectors: z.string().nullable().default(null),
    declaredRtpPct: z.number().nullable().default(null),
    doIntegrity: z.boolean().default(false),
    doRoundTypes: z.boolean().default(false),
    math: stakeMathGateSchema.default({}),
    integrity: stakeIntegrityGateSchema.default({}),
    fair: stakeFairGateSchema.default({}),
    rounds: stakeRoundGateSchema.default({}),
    updatedAt: z.string().nullable().default(null),
    error: z.string().nullable().default(null),
});
/**
 * One row of the AI-generated event-type glossary: a raw code event `type`
 * (e.g. `weaponReveal`) mapped to a player-facing `name` and a one-line
 * `meaning`, grounded in the analysed game-help screens. Glossary is
 * game-wide because the studio's event names are identical across bet modes.
 */
export const stakeEventGlossSchema = z.object({
    type: z.string(),
    name: z.string(),
    meaning: z.string(),
});
/** AI "storytelling" narrative (Markdown) for a single bet mode. */
export const stakeModeNarrativeSchema = z.object({
    mode: z.string(),
    narrative: z.string(),
});
/**
 * Persisted, on-demand AI analysis of the round-type data: a game-wide event
 * glossary plus per-mode narratives. Generated lazily (per expanded mode) and
 * stored next to the verifier report so the chat can reference it.
 */
export const stakeRoundExplainSchema = z.object({
    status: z.enum(['idle', 'running', 'ready', 'error']).default('idle'),
    glossary: z.array(stakeEventGlossSchema).default([]),
    modes: z.array(stakeModeNarrativeSchema).default([]),
    model: z.string().nullable().default(null),
    generatedAt: z.string().nullable().default(null),
    error: z.string().nullable().default(null),
});
export const stakeArtifactsSchema = z.object({
    report: stakeReportSchema.nullable().default(null),
    explain: stakeRoundExplainSchema.nullable().default(null),
});
/** Where stake-engine progress can originate from. */
export const stakeStageSchema = z.enum(['verify', 'chat', 'explain', 'scenarios']);
// AI Critical-File Nominator (Phase 3 helper) --------------------------------
/** What role a nominated file plays in the payout / RNG chain. */
export const criticalAiCategorySchema = z.enum([
    'reelset',
    'weights',
    'paytable',
    'config',
    'rtp',
    'rng',
    'chain',
    'other',
]);
/** One AI-nominated critical file, with on-disk (server-computed) SHA1s. */
export const criticalAiItemSchema = z.object({
    filename: z.string(),
    /** POSIX-relative path inside the pulled package. */
    path: z.string(),
    /** Normalised SHA1 (CRLF->LF for text), matching TEXEC comparison. */
    sha1: z.string(),
    /** Raw-bytes SHA1 for reference. */
    sha1Raw: z.string(),
    category: criticalAiCategorySchema,
    /** AI explanation of why this file is payout-critical. */
    reason: z.string(),
});
/** Persisted result of one AI critical-file nomination run. */
export const criticalAiResultSchema = z.object({
    status: z.enum(['idle', 'running', 'done', 'error']).default('idle'),
    model: z.string().nullable().default(null),
    generatedAt: z.string().nullable().default(null),
    /** Short AI narrative of the RNG -> game-config chain it traced. */
    chainSummary: z.string().default(''),
    items: z.array(criticalAiItemSchema).default([]),
    error: z.string().nullable().default(null),
});
// Scenario / debug viewer (per-mode book records) ----------------------------
/** Coarse outcome class derived from payout + criteria. */
export const stakeScenarioWinTypeSchema = z.enum(['dead', 'sub', 'win', 'wincap']);
/**
 * One compact, list-friendly row per book record. Heavy fields (board, full
 * events) live only in the detail endpoint; this is what the filterable table
 * is built from. Mechanics are game-agnostic: `features` are the optional
 * mechanic keys present this round (derived per game from the book), and
 * `attrs` are categorical attribute values discovered per game (e.g. weapon).
 */
export const stakeScenarioRowSchema = z.object({
    id: z.number().int(),
    criteria: z.string(),
    /** payoutMultiplier / 100, i.e. the x-multiple of the stake. */
    multiplier: z.number(),
    /** Lookup weight as a string (can exceed Number-safe range). */
    weight: z.string().default('0'),
    /** 1-in-N odds for this exact record (totalWeight / weight); null if unknown. */
    oneIn: z.number().nullable().default(null),
    winType: stakeScenarioWinTypeSchema,
    /** Optional-mechanic keys present this round (profile featureTag keys). */
    features: z.array(z.string()).default([]),
    /** Categorical attribute values this round (profile attribute key -> value). */
    attrs: z.record(z.string()).default({}),
    /** Sorted distinct event types joined by '+', the structural signature. */
    eventSig: z.string().default(''),
    numWins: z.number().int().default(0),
    shapeKey: z.string().default(''),
});
export const stakeScenarioFacetCountSchema = z.object({
    value: z.string(),
    count: z.number().int(),
});
export const stakeScenarioMultBandSchema = z.object({
    label: z.string(),
    lo: z.number(),
    hi: z.number().nullable(),
    count: z.number().int(),
});
/** A discovered optional-mechanic feature facet (key + display label + count). */
export const stakeScenarioFeatureFacetSchema = z.object({
    key: z.string(),
    label: z.string(),
    count: z.number().int(),
});
/** A discovered categorical attribute (e.g. weapon) with its value counts. */
export const stakeScenarioAttributeFacetSchema = z.object({
    key: z.string(),
    label: z.string(),
    values: z.array(stakeScenarioFacetCountSchema).default([]),
});
export const stakeScenarioFacetsSchema = z.object({
    winTypes: z.array(stakeScenarioFacetCountSchema).default([]),
    /** Game-agnostic optional-mechanic features present in this mode. */
    features: z.array(stakeScenarioFeatureFacetSchema).default([]),
    /** Game-agnostic categorical attributes discovered in this mode. */
    attributes: z.array(stakeScenarioAttributeFacetSchema).default([]),
    criteria: z.array(stakeScenarioFacetCountSchema).default([]),
    eventTypes: z.array(stakeScenarioFacetCountSchema).default([]),
    multBands: z.array(stakeScenarioMultBandSchema).default([]),
    maxMultiplier: z.number().default(0),
});
/**
 * Per-mode structural profile derived from the game's own book: which optional
 * mechanics (features) and categorical attributes exist, plus capability flags
 * that drive the detail view and summaries generically. `labels` maps event
 * type / attribute keys to display names (glossary-first, else humanized).
 */
export const stakeScenarioProfileSchema = z.object({
    capabilities: z
        .object({ hasBoard: z.boolean().default(false), hasItemisedWins: z.boolean().default(false) })
        .default({ hasBoard: false, hasItemisedWins: false }),
    /** Optional-mechanic event types. `rare` ones (uncommon) are surfaced in summaries. */
    features: z
        .array(z.object({ key: z.string(), label: z.string(), rare: z.boolean().default(false) }))
        .default([]),
    /** Categorical attributes; `sourceType`/`field` say where to read the value. */
    attributes: z
        .array(z.object({ key: z.string(), label: z.string(), sourceType: z.string(), field: z.string() }))
        .default([]),
    labels: z.record(z.string()).default({}),
});
/** A distinct round-shape (criteria + event signature + multiplier band). */
export const stakeScenarioShapeSchema = z.object({
    shapeKey: z.string(),
    criteria: z.string(),
    eventSig: z.string(),
    multBand: z.string(),
    count: z.number().int().default(0),
    weight: z.string().default('0'),
    exampleId: z.number().int().default(0),
});
export const stakeScenarioMetaSchema = z.object({
    mode: z.string(),
    totalRecords: z.number().int().default(0),
    totalWeight: z.string().default('0'),
    cost: z.number().default(0),
    facets: stakeScenarioFacetsSchema.default({}),
    profile: stakeScenarioProfileSchema.default({}),
    shapes: z.array(stakeScenarioShapeSchema).default([]),
    builtAt: z.string().nullable().default(null),
});
/** A single board cell for the grid renderer. */
export const stakeScenarioCellSchema = z.object({
    name: z.string(),
    multiplier: z.number().nullable().default(null),
    bomb: z.boolean().default(false),
    scatter: z.boolean().default(false),
});
/** One labelled step in the deterministic event timeline. */
export const stakeScenarioStepSchema = z.object({
    index: z.number().int().nullable().default(null),
    type: z.string(),
    label: z.string(),
    detail: z.string().default(''),
});
export const stakeScenarioSummarySchema = z.object({
    id: z.number().int(),
    criteria: z.string(),
    multiplier: z.number(),
    winType: stakeScenarioWinTypeSchema,
    oneIn: z.number().nullable().default(null),
    /** Optional-mechanic feature keys present this round. */
    features: z.array(z.string()).default([]),
    /** Categorical attribute values this round (attribute key -> value). */
    attrs: z.record(z.string()).default({}),
    paidSymbols: z.array(z.object({ symbol: z.string(), win: z.number() })).default([]),
    steps: z.array(stakeScenarioStepSchema).default([]),
    /** Reels x rows; null for games without a board (crash/instant). */
    board: z.array(z.array(stakeScenarioCellSchema)).nullable().default(null),
});
export const stakeScenarioDetailSchema = z.object({
    mode: z.string(),
    shapeKey: z.string().nullable().default(null),
    summary: stakeScenarioSummarySchema,
    /** Raw record JSON for the "show raw" view. */
    raw: z.unknown().nullable().default(null),
    /** Shape template or on-demand single-round AI text, if generated. */
    explanation: z.string().nullable().default(null),
    /**
     * Where `explanation` came from, so the UI can flag shared vs precise text:
     *  - bespoke: per-round AI, validated against this round's facts
     *  - template: shape template filled for this round, validated
     *  - deterministic: never-wrong factual line (AI missing or withheld)
     */
    explanationSource: z
        .enum(['bespoke', 'template', 'deterministic'])
        .nullable()
        .default(null),
});
/** Cached per-shape AI "what to expect" explanations. */
export const stakeScenarioShapeExplainSchema = z.object({
    mode: z.string(),
    model: z.string().nullable().default(null),
    generatedAt: z.string().nullable().default(null),
    items: z
        .array(z.object({
        shapeKey: z.string(),
        /** Generic shape-level explanation (example round filled in). */
        expect: z.string(),
        /**
         * Reusable explanation with {placeholders} that are substituted per
         * round from deterministic facts, giving instant round-specific text
         * without a per-round AI call.
         */
        template: z.string().default(''),
    }))
        .default([]),
});
/** Server response for a filtered/paged rows query. */
export const stakeScenarioRowsPageSchema = z.object({
    rows: z.array(stakeScenarioRowSchema).default([]),
    total: z.number().int().default(0),
    page: z.number().int().default(0),
    pageSize: z.number().int().default(50),
});
// Bug write & review (Phase 5) -----------------------------------------------
/**
 * Reference to a single bug attachment the user wants uploaded to the new
 * Jira issue. The bug-create handler resolves each ref into raw bytes by
 * `source` + `id`:
 *   - `game-rules-image` -> `{runRoot}/images/<id>` (filename)
 *   - `video-frame`      -> `{runRoot}/videos/<id>` (relative path under videos/)
 *   - `chat-evidence`    -> chat-evidence store id for the current test
 *   - `upload`           -> bug staging upload id under `{runRoot}/.bug-uploads/`
 */
export const bugAttachmentRefSchema = z.object({
    source: z.enum(['game-rules-image', 'video-frame', 'chat-evidence', 'upload']),
    id: z.string().min(1),
    label: z.string().optional(),
    mime: z.string().optional(),
});
/**
 * Output of the "draft bug from chat" endpoint: an editable suggestion the
 * user can refine inside the BugBuilderModal before it's posted to Jira.
 *
 * - `summary` is `"<GameName> - <short issue>"` (server prepends GameName
 *   when the model leaves it out).
 * - `descriptionMd` focuses on expected vs actual behaviour, NO TLIB-*
 *   mentions; REQ-* is also avoided since those go in the Requirements
 *   custom field.
 * - `stepsToReproduce` is first-class (it now maps to Jira's
 *   `customfield_10209`).
 * - `environment` is a short URL / build hint the user reviews before
 *   submission; maps to the Jira built-in `environment` field.
 * - `citedRequirementKeys` is the AI's suggestion for the Requirements
 *   custom field (server intersects it with the jurisdiction-filtered
 *   REQ list before sending).
 * - `suggestedAttachments` lets the AI nominate frames / images that
 *   illustrate the bug; the user can accept / remove them in the modal.
 */
export const bugDraftSchema = z.object({
    summary: z.string(),
    descriptionMd: z.string(),
    severity: z.enum(['minor', 'major', 'blocker']).default('major'),
    stepsToReproduce: z.array(z.string()).default([]),
    environment: z.string().default(''),
    citedRequirementKeys: z.array(z.string()).default([]),
    suggestedAttachments: z.array(bugAttachmentRefSchema).default([]),
});
/**
 * Single file the user attached to the chat for a specific test. Stored
 * server-side under `{runRoot}/chat-evidence/{testKey}/` with a sidecar
 * `index.jsonl`. Images are run through the vision analyser at upload
 * time so the chat AI can read them on the very next turn; text-like
 * files are inlined into `analysis`; other formats are stored only.
 *
 * The same items also surface in the bug-attachment picker so the user
 * can ship them to Jira along with the new bug.
 */
export const chatEvidenceItemSchema = z.object({
    id: z.string().min(1),
    filename: z.string(),
    mime: z.string(),
    size: z.number().int().nonnegative(),
    kind: z.enum(['image', 'text', 'other']),
    uploadedAt: z.string().datetime(),
    analysis: z.string().nullable(),
    messageId: z.string().nullable(),
});
export const jiraAttachmentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimeType: z.string().nullable(),
    size: z.number().nullable(),
    contentUrl: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    createdAt: z.string().nullable(),
    author: z.string().nullable(),
});
export const jiraCommentSchema = z.object({
    id: z.string(),
    author: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string().nullable(),
    bodyMd: z.string(),
});
export const jiraTransitionSchema = z.object({
    id: z.string(),
    name: z.string(),
    toStatus: z.string(),
    toCategory: z.string().nullable().default(null),
});
/**
 * Full detail row used by the Phase 5 review pane. `linkedTestKey` is the
 * TLIB this bug was raised from (when known), so the review pane can quote
 * the originating test review when chatting with the AI.
 */
export const bugDetailSchema = z.object({
    key: z.string(),
    summary: z.string(),
    status: z.string().nullable(),
    statusCategory: z.string().nullable().default(null),
    resolution: z.string().nullable(),
    priority: z.string().nullable(),
    reporter: z.string().nullable(),
    assignee: z.string().nullable(),
    labels: z.array(z.string()).default([]),
    descriptionMd: z.string().nullable(),
    comments: z.array(jiraCommentSchema).default([]),
    attachments: z.array(jiraAttachmentSchema).default([]),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    url: z.string().nullable(),
    fetchedAt: z.string(),
    linkedTestKey: z.string().nullable().default(null),
    isDone: z.boolean().default(false),
});
/**
 * Compact list-item shape for the Phase 5 panel rail. The detail pane fetches
 * the full {@link BugDetail} on demand; the list endpoint returns these so
 * the panel can render quickly even when a TEXEC has 30+ bugs linked.
 */
export const bugReviewItemSchema = z.object({
    key: z.string(),
    summary: z.string(),
    status: z.string().nullable(),
    statusCategory: z.string().nullable().default(null),
    priority: z.string().nullable(),
    assignee: z.string().nullable(),
    reporter: z.string().nullable(),
    labels: z.array(z.string()).default([]),
    url: z.string().nullable(),
    isDone: z.boolean(),
    linkedTestKey: z.string().nullable().default(null),
    commentCount: z.number().default(0),
    attachmentCount: z.number().default(0),
    updatedAt: z.string().nullable().default(null),
});
export const bugReviewBundleMetaSchema = z.object({
    fetchedAt: z.string(),
    stale: z.boolean(),
    staleReason: z.string().optional(),
    jiraHost: z.string().optional(),
});
export const bugReviewBundleSchema = z.object({
    texecKey: z.string(),
    items: z.array(bugReviewItemSchema),
    meta: bugReviewBundleMetaSchema.optional(),
});
// Gameplay-video analyzer (Extras tool) -------------------------------------
//
// Each video uploaded against a run is split into 1 fps JPEG frames and the
// per-frame OpenAI vision output is persisted in a single JSON file. The
// `second` field doubles as the frame index since extraction is fixed at
// 1 fps; this lets the UI's HTML5 player jump to / highlight the matching
// row purely from `video.currentTime`.
export const labelConfidenceSchema = z.enum(['low', 'medium', 'high']);
export const videoAnalysisProviderSchema = z.enum(['openai-frames', 'gemini-native']);
export const videoAnalysisFrameSchema = z.object({
    /** Zero-based seconds offset from the start of the video. */
    second: z.number().int().nonnegative(),
    /** Frame filename (e.g. `frame_00012.jpg`), relative to the frames dir. */
    frame: z.string(),
    /** AI text describing what is on screen at this second. */
    text: z.string(),
    /** True when this frame failed to analyse; `text` is the error message. */
    error: z.boolean().default(false),
    /**
     * Evidence-label keys assigned by the second-pass tagger
     * (`services/video/tagFrames.ts`). Drawn from the visible subset of the
     * evidence-label taxonomy (`game-rules`, `ui-controls`, `gp-base-game`,
     * `gp-bonus-features`, `gp-gamble`, `autoplay`, `jackpot-game`, `JS`).
     * Empty array on legacy frames analysed before the tagger landed or
     * when the tagger call failed - the prompt-builder treats such frames
     * as "untagged" and routes them into the fallback widening path.
     */
    labels: z.array(z.string()).default([]),
    /** Tagger's self-reported confidence in the assigned labels. */
    labelConfidence: labelConfidenceSchema.nullable().default(null),
});
/**
 * Sidecar JSON written alongside the static-image OCR cache
 * (`analysis_log.txt`) to record the tagger's verdict for every Drive
 * screenshot in a folder. Keyed by basename so the existing OCR cache file
 * stays untouched and the tagger can be re-run independently.
 */
export const imageLabelsSidecarSchema = z.object({
    version: z.literal(1),
    entries: z.record(z.object({
        labels: z.array(z.string()),
        confidence: labelConfidenceSchema.nullable(),
    })),
});
/**
 * One entry in the evidence-label taxonomy. Mirrors the Jira "Indicator"
 * label table verbatim - each entry pairs a Jira label key (e.g.
 * `gp-base-game`) with its definition, the type of evidence that proves
 * it, and an `isVisual` flag that gates whether the tagger may ever
 * assign it to a frame / image.
 */
export const evidenceLabelSchema = z.object({
    id: z.string().min(1),
    /** Jira label key, e.g. `gp-base-game`. Stored case-insensitively but rendered as-typed. */
    key: z.string().min(1).max(40),
    /** Short human-friendly name, e.g. "Base round lifecycle". */
    name: z.string().max(120),
    /**
     * Verbatim definition from the Jira label table. Used as both the
     * tagger prompt's per-label rubric and the aggregator's "Why these
     * frames" header so both surfaces read the same wording.
     */
    definition: z.string().max(4000),
    /** "Video", "Internal Jira link", "Data + result", etc. */
    evidenceType: z.string().max(200),
    /**
     * True iff this label can be assigned to a video frame or screenshot.
     * `ref-link-i`, `measurement`, `source-code-review`, ... are non-visual
     * and are stored for completeness but never appear on the tagger menu.
     */
    isVisual: z.boolean(),
    updatedAt: z.string(),
});
export const evidenceLabelBundleSchema = z.object({
    version: z.literal(1),
    entries: z.array(evidenceLabelSchema),
});
export const videoAnalysisSchema = z.object({
    /** Filesystem-safe slug used for the file/frames-dir/analysis-json triple. */
    slug: z.string(),
    /** Analysis provider used to generate this cache file. */
    provider: videoAnalysisProviderSchema.default('gemini-native'),
    /** Original filename as uploaded by the user. */
    videoFile: z.string(),
    /**
     * Whole-second duration parsed from ffmpeg; `null` when ffmpeg never
     * emitted a Duration line we could parse.
     */
    durationSeconds: z.number().int().nonnegative().nullable(),
    /** Frames per second of the extraction (fixed at 1 for now). */
    fps: z.number().int().positive(),
    /** Model used for the per-frame vision call (so re-analyse upgrades are auditable). */
    model: z.string(),
    /** Whether rows were produced from frame OCR or native video understanding. */
    sourceMode: z.enum(['frame-by-frame', 'native-video']).default('frame-by-frame'),
    /** ISO timestamp when the JSON was last (re-)written. */
    createdAt: z.string(),
    /** Optional token/cost telemetry attached by the provider. */
    usage: z
        .object({
        promptTokens: z.number().int().nonnegative().optional(),
        completionTokens: z.number().int().nonnegative().optional(),
        totalTokens: z.number().int().nonnegative().optional(),
        estimatedCostEur: z.number().nonnegative().optional(),
    })
        .nullable()
        .optional(),
    /**
     * One entry per extracted frame, ordered by `second` ascending. The
     * length equals the total frame count; failed frames keep their slot so
     * the row count stays stable across re-runs.
     */
    frames: z.array(videoAnalysisFrameSchema),
});
/** List-row shape returned by `GET /api/runs/:id/videos`. */
export const videoListItemSchema = z.object({
    slug: z.string(),
    videoFile: z.string(),
    sizeBytes: z.number().nonnegative(),
    uploadedAt: z.string(),
    /** Number of frames extracted to disk (0 before extraction). */
    frameCount: z.number().nonnegative(),
    /** Duration from a successful analysis run; null when not yet analysed. */
    durationSeconds: z.number().nullable(),
    /** True when `video-analysis.<slug>.json` exists. */
    analysisExists: z.boolean(),
    /**
     * Provider-specific analysis availability for comparison mode. Keys are
     * provider ids (`openai-frames`, `gemini-native`).
     */
    analysisByProvider: z
        .object({
        'openai-frames': z.boolean().default(false),
        'gemini-native': z.boolean().default(false),
    })
        .default({ 'openai-frames': false, 'gemini-native': false }),
    /** Status flags for the rail / list. */
    extractInProgress: z.boolean().default(false),
    analyzeInProgress: z.boolean().default(false),
    /**
     * Live extract/analyse snapshot when a job is running. `startedAt` is the
     * server wall-clock start so the UI ETA survives leaving the page.
     */
    jobProgress: z
        .object({
        kind: z.enum(['extract', 'analyze']),
        provider: videoAnalysisProviderSchema.optional(),
        current: z.number(),
        total: z.number().nullable(),
        completed: z.number().nonnegative().default(0),
        errors: z.number().nonnegative().default(0),
        status: z.enum(['started', 'progress']),
        startedAt: z.string(),
        message: z.string().optional(),
    })
        .nullable()
        .optional(),
});
export const wsServerMessageSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('log'),
        runId: z.string().nullable(),
        level: z.enum(['info', 'warn', 'error', 'success']),
        message: z.string(),
        ts: z.string(),
    }),
    z.object({
        type: z.literal('phase'),
        runId: z.string(),
        phase: phaseSchema,
        status: phaseStatusSchema,
        progress: z.number().nullable().optional(),
        message: z.string().optional(),
    }),
    z.object({
        type: z.literal('test-update'),
        runId: z.string(),
        testKey: z.string(),
        review: testReviewSchema.partial(),
    }),
    z.object({
        type: z.literal('analyze-progress'),
        runId: z.string(),
        current: z.number(),
        total: z.number(),
        completed: z.number(),
        errors: z.number(),
        stopped: z.boolean().optional(),
    }),
    z.object({
        type: z.literal('test-analyze-status'),
        runId: z.string(),
        testKey: z.string(),
        status: z.enum(['started', 'completed', 'failed']),
        message: z.string().optional(),
    }),
    // Per-test source-code "scout" pass progress. Emitted once per scouted file
    // (`current`/`total` indexes which file just finished). The aggregator
    // (flagship) call is reported via the existing `test-analyze-status`
    // events; this sub-stream only covers the parallel scout phase so the UI
    // can render "Scouting 3/6: FruityDiamondStacks_96.js" under the main
    // "Analyzing TLIB-179..." banner.
    z.object({
        type: z.literal('test-scout-progress'),
        runId: z.string(),
        testKey: z.string(),
        current: z.number(),
        total: z.number(),
        filename: z.string(),
        status: z.enum(['started', 'completed', 'failed', 'cached']),
        message: z.string().optional(),
    }),
    z.object({
        type: z.literal('bug-update'),
        runId: z.string(),
        bugKey: z.string(),
        patch: bugReviewItemSchema.partial(),
    }),
    z.object({
        type: z.literal('precondition-update'),
        runId: z.string(),
        reason: z.enum(['pull', 'stage', 'push', 'verify']),
    }),
    z.object({
        type: z.literal('run-deleted'),
        runId: z.string(),
    }),
    // Gameplay-video analyzer (Extras tool) -----------------------------------
    //
    // Two separate progress streams because extraction and vision analysis run
    // on very different time scales: ffmpeg burns through 900 frames in a few
    // seconds while the per-frame OpenAI calls take 5-15 minutes for the same
    // clip. Splitting them lets the UI render two independent progress bars
    // and lets the user start the analyse step before extraction finishes
    // visually (it can't actually overlap; the slug is reused as the
    // correlation id so the panel knows which video each tick refers to).
    z.object({
        type: z.literal('video-extract-progress'),
        runId: z.string(),
        slug: z.string(),
        current: z.number(),
        total: z.number().nullable(),
        status: z.enum(['started', 'progress', 'completed', 'failed']),
        message: z.string().optional(),
        startedAt: z.string().optional(),
    }),
    z.object({
        type: z.literal('video-analyze-progress'),
        runId: z.string(),
        slug: z.string(),
        provider: videoAnalysisProviderSchema.default('gemini-native'),
        current: z.number(),
        total: z.number(),
        completed: z.number(),
        errors: z.number(),
        status: z.enum(['started', 'progress', 'completed', 'failed']),
        message: z.string().optional(),
        startedAt: z.string().optional(),
    }),
    // Emitted by the second-pass evidence-label tagger that runs after each
    // successful per-frame OCR. Mirrors the `video-analyze-progress` shape so
    // the Extras Video panel can render a separate "Tagging frames" bar
    // beneath the existing "Analyzing frames" bar without bespoke handling.
    z.object({
        type: z.literal('video-tag-progress'),
        runId: z.string(),
        slug: z.string(),
        provider: videoAnalysisProviderSchema.default('gemini-native'),
        current: z.number(),
        total: z.number(),
        completed: z.number(),
        errors: z.number(),
        status: z.enum(['started', 'progress', 'completed', 'failed']),
        message: z.string().optional(),
    }),
    z.object({
        type: z.literal('video-native-stage-progress'),
        runId: z.string(),
        slug: z.string(),
        provider: z.literal('gemini-native'),
        stage: z.enum(['upload', 'analyze', 'cleanup']),
        status: z.enum(['started', 'progress', 'completed', 'failed']),
        message: z.string().optional(),
    }),
    // Game Knowledge Base (Extras): one stream for the short description, the
    // long report, and the dedicated chat. `tool` carries a short label of the
    // file operation the model is performing (e.g. "read_file: gamelib.h") so
    // the UI can show a live "tracing the package" activity line.
    z.object({
        type: z.literal('game-kb-progress'),
        runId: z.string(),
        stage: gameKbStageSchema,
        status: z.enum(['started', 'progress', 'completed', 'failed']),
        message: z.string().optional(),
        tool: z.string().optional(),
    }),
    // Stake Engine verification (Extras, EAS only): one stream for the gate run
    // and the dedicated chat. `tool` carries the file operation the chat model
    // just performed so the UI can show a live "tracing the package" line.
    z.object({
        type: z.literal('stake-engine-progress'),
        runId: z.string(),
        stage: stakeStageSchema,
        status: z.enum(['started', 'progress', 'completed', 'failed']),
        message: z.string().optional(),
        tool: z.string().optional(),
    }),
    z.object({
        type: z.literal('look-at-that-progress'),
        runId: z.string(),
        event: z.string(),
        sessionId: z.string().optional(),
        message: z.string().optional(),
        screenshot: z.string().optional(),
        width: z.number().nullable().optional(),
        height: z.number().nullable().optional(),
        awaitingCredentials: z.boolean().optional(),
        recipeUser: z.string().nullable().optional(),
        exploreItem: z
            .object({
            id: z.string(),
            prompt: z.string(),
            explanation: z.string(),
            file: z.string(),
            ts: z.string(),
            sessionId: z.string().optional(),
        })
            .optional(),
        actionType: z.string().optional(),
        success: z.boolean().optional(),
        /** Action coordinates in screenshot space, for the bot-cursor overlay. */
        x: z.number().optional(),
        y: z.number().optional(),
        /** Set on play_test / play_result / queue events. */
        testKey: z.string().optional(),
        playVerdict: z.string().optional(),
        /** Remaining queue after this event (play queue lifecycle). */
        queuedKeys: z.array(z.string()).optional(),
        /** New game-memory notes learned by the last task. */
        memoryNotes: z.array(z.string()).optional(),
        adapter: z.string().optional(),
        checklist: z
            .array(z.object({
            id: z.string(),
            label: z.string(),
            status: z.enum(['pending', 'active', 'done', 'stuck']),
            detail: z.string().optional(),
        }))
            .optional(),
        stuckStep: z.string().nullable().optional(),
    }),
    // AI Critical-File Nominator (Phase 3 helper): the model traces the payout /
    // RNG chain across the pulled source. `tool` carries the file operation it is
    // performing so the UI can show a live "tracing the package" activity line.
    z.object({
        type: z.literal('critical-ai-progress'),
        runId: z.string(),
        status: z.enum(['started', 'progress', 'completed', 'failed']),
        message: z.string().optional(),
        tool: z.string().optional(),
    }),
    z.object({
        type: z.literal('auto-workflow-progress'),
        runId: z.string(),
        step: z.string(),
        status: z.enum(['started', 'progress', 'completed', 'failed', 'skipped']),
        message: z.string().optional(),
        running: z.boolean(),
    }),
    // Hint for the "My active work" strip: refetch `/api/me/active-work`.
    // `runId` is always null so every subscribed client receives it.
    z.object({
        type: z.literal('active-work'),
        runId: z.null(),
        ts: z.string(),
    }),
]);
export const autoWorkflowPlanSchema = z.object({
    populateTexec: z.boolean(),
    envLinks: z.boolean(),
    pullSource: z.boolean(),
    images: z.boolean(),
    video: z.boolean(),
    gameKb: z.boolean(),
    gameKbDescription: z.boolean(),
    gameKbReport: z.boolean(),
    texec: z.boolean(),
    texecMode: z.enum(['all', 'tba']),
});
export const DEFAULT_AUTO_WORKFLOW_PLAN = {
    populateTexec: true,
    envLinks: true,
    pullSource: true,
    images: true,
    video: true,
    gameKb: true,
    gameKbDescription: true,
    gameKbReport: true,
    texec: true,
    texecMode: 'tba',
};
/** Force pull/images when TEXEC is on, and pull + at least one KB artifact when Game Knowledge is on. */
export function normalizeAutoWorkflowPlan(plan) {
    let next = { ...plan };
    if (next.texec) {
        next.pullSource = true;
        next.images = true;
    }
    if (next.gameKb) {
        next.pullSource = true;
        if (!next.gameKbDescription && !next.gameKbReport)
            next.gameKbDescription = true;
    }
    else {
        next.gameKbDescription = false;
        next.gameKbReport = false;
    }
    return next;
}
export const autoWorkflowStatusSchema = z.object({
    running: z.boolean(),
    step: z.string().nullable(),
    message: z.string().nullable(),
    error: z.string().nullable(),
    plan: autoWorkflowPlanSchema.nullable(),
});
/** One run with in-flight queued jobs and/or non-queue busy flags. */
export const activeWorkJobSchema = z.object({
    id: z.string(),
    type: z.string(),
    status: z.enum(['queued', 'running', 'completed', 'failed', 'cancelled']),
});
export const activeWorkRunSchema = z.object({
    runId: z.string(),
    srKey: z.string(),
    texecKey: z.string(),
    studio: z.string(),
    gameName: z.string(),
    jobs: z.array(activeWorkJobSchema),
    flags: z.object({
        texecAnalyzing: z.boolean(),
        videoBusy: z.boolean(),
        imagesBusy: z.boolean(),
        pullSourceBusy: z.boolean(),
        driveVideoBusy: z.boolean(),
        autoWorkflowBusy: z.boolean().optional(),
    }),
    /** Who is currently driving Sidekick work on this run (jobs / auto-workflow). */
    workerEmail: z.string().nullable().optional(),
    workerName: z.string().nullable().optional(),
    /** Initials for the worker, e.g. MM for Mihael Mladenov. */
    initials: z.string().nullable().optional(),
    /** Current auto-workflow step when autoWorkflowBusy. */
    autoWorkflowStep: z.string().nullable().optional(),
    autoWorkflowMessage: z.string().nullable().optional(),
});
export const activeWorkResponseSchema = z.object({
    runs: z.array(activeWorkRunSchema),
});
export const wsClientMessageSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('subscribe'), runId: z.string().nullable() }),
    z.object({ type: z.literal('ping') }),
]);
// Phase 4 - Generate Environment Links --------------------------------------
export const studioRecipeSchema = z.object({
    studio: z.string(),
    version: z.number().int().positive().default(1),
    /**
     * How to interpret the `Play URL` field on the Game task:
     *  - "full"   - Game task's Play URL is already a launch URL; we keep it as-is
     *               (the recipe may still merge in missing query params).
     *  - "base"   - Game task's Play URL is a launcher/lobby host; we apply
     *               `urlTemplate` on top of it via `{{playUrlBase}}`.
     *  - "ignore" - Ignore the Game task Play URL entirely and use
     *               `playUrlBase` from the recipe defaults.
     */
    playUrlMode: z.enum(['full', 'base', 'ignore']).default('base'),
    urlTemplate: z.string(),
    /** Optional fallback used when playUrlMode === "ignore" or the Game task has no Play URL. */
    playUrlBase: z.string().optional(),
    /** Map TEXEC market codes (e.g. "MGA") to URL-shaped market codes (e.g. "mt"). */
    marketCodeMap: z.record(z.string(), z.string()).default({}),
    /** Default placeholder values; merged with perMarketOverrides[market]. */
    defaults: z
        .object({
        lang: z.string().nullable().default('en'),
        currency: z.string().nullable().default(null),
        notes: z.string().nullable().default(null),
        credentials: z
            .object({
            user: z.string().nullable().default(null),
            password: z.string().nullable().default(null),
        })
            .partial()
            .default({}),
    })
        .partial()
        .default({}),
    /** Per-market overrides, keyed by TEXEC market code (upper-case). */
    perMarketOverrides: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
    /** Markdown launch guide with `{{placeholder}}` substitutions. */
    launchGuideMd: z.string(),
    /**
     * Provenance for this recipe. `null` for hand-edited recipes; populated
     * when the recipe was drafted by the AI from a known source so the UI can
     * show "Synced from Confluence X minutes ago" / stale badges.
     */
    source: z
        .object({
        kind: z.enum(['pdf', 'confluence']),
        confluencePageId: z.string().nullable().default(null),
        confluencePageTitle: z.string().nullable().default(null),
        confluenceSpaceKey: z.string().nullable().default(null),
        confluenceVersion: z.number().nullable().default(null),
        /** ISO timestamp of the last successful sync. */
        lastSyncedAt: z.string().nullable().default(null),
    })
        .nullable()
        .default(null),
    /**
     * AI-summarised markdown brief of the studio's wiki page. Generated once
     * per Confluence sync; injected as system context into every downstream
     * AI call that runs for a TEXEC belonging to this studio (precondition
     * verifiers, Xray test analyser, bug-review chat). Null until the studio
     * is connected to Confluence at least once.
     */
    clientBriefMd: z.string().nullable().default(null),
    /**
     * Operator-written notes for AI surfaces (Look at that, Phase 7, bug
     * chat, video analysis). Per-studio chrome: hamburger vs autoplay,
     * debug/cheat menus, typical HUD layout. Empty/null = no extra context.
     * Not the Confluence auto-brief (`clientBriefMd`).
     */
    aiKnowledgeMd: z.string().nullable().default(null),
    /**
     * GitLab project the Phase 3 "Pull Source Code" step clones from for
     * this studio. Optional - when the whole `gitlab` block (or either
     * `projectId` / `branch` field) is null, the server falls back to the
     * legacy hard-coded `STUDIOS_INFO` map. New studios added entirely
     * through the Studios page MUST populate this; bundled studios can
     * leave it null and rely on the seed map.
     */
    gitlab: z
        .object({
        /** Numeric GitLab project id (string so JSON stays stable). */
        projectId: z.string().nullable().default(null),
        /** Default branch to clone. Defaults to "master" when empty. */
        branch: z.string().nullable().default(null),
    })
        .nullable()
        .default(null),
});
/**
 * User-maintained domain glossary entry. Lets the user define what "Spin
 * button", "Buy Chance Feature", "Ante Bet", etc. mean for this app so the
 * various AI surfaces (frame analyser, image analyser, test aggregator,
 * chat) align on a single vocabulary instead of guessing.
 */
export const glossaryEntrySchema = z.object({
    id: z.string().min(1),
    term: z.string().min(1).max(120),
    description: z.string().min(1).max(2000),
    updatedAt: z.string().datetime(),
});
export const glossarySchema = z.object({
    version: z.literal(1),
    entries: z.array(glossaryEntrySchema),
});
// Ontology - slot-concept catalogue -----------------------------------------
//
// The structured replacement for the flat glossary. Each concept is a
// jurisdiction-agnostic record with the "teeth" a glossary lacks: a
// functional `invariant` (what the thing DOES, so creatively-branded
// instances still map), `aliases` (the 90% fast path), behavioural / visual /
// code `signals`, `relations` for disambiguation from near-neighbours, and
// labelled `examples`. Regulatory rules hang off it as POINTERS only
// (`regulatoryRelevance` carries `{jurisdiction, reqKey, rule}` - never the
// rule text, which stays in Jira). Persisted relationally in a per-module
// SQLite database; this Zod shape is the assembled DTO / API contract.
export const conceptCategorySchema = z.enum([
    'mechanic',
    'ui-element',
    'menu-type',
    'math-notion',
    'meta',
]);
export const conceptStatusSchema = z.enum(['active', 'draft', 'deprecated']);
export const conceptAliasKindSchema = z.enum(['generic', 'branding']);
export const conceptSignalTypeSchema = z.enum(['visual', 'behavioral', 'code']);
export const conceptRelationKindSchema = z.enum([
    'not_to_be_confused_with',
    'parent',
    'child',
]);
export const conceptExampleLabelSchema = z.enum(['positive', 'negative']);
export const conceptRegulatoryRuleSchema = z.enum([
    'forbidden',
    'required',
    'conditional',
]);
export const conceptAliasSchema = z.object({
    text: z.string().min(1).max(160),
    kind: conceptAliasKindSchema.default('generic'),
    /** Set when this alias is a specific studio's branding, e.g. "SuperSpin Boost". */
    studio: z.string().max(120).nullable().default(null),
});
export const conceptSignalSchema = z.object({
    type: conceptSignalTypeSchema,
    text: z.string().min(1).max(600),
});
export const conceptRelationSchema = z.object({
    relatedId: z.string().min(1),
    relation: conceptRelationKindSchema,
    note: z.string().max(600).nullable().default(null),
});
export const conceptExampleSchema = z.object({
    /** Filename under data/ontology/examples/, or a run-relative evidence ref. */
    imageRef: z.string().min(1),
    label: conceptExampleLabelSchema,
    game: z.string().max(160).nullable().default(null),
    note: z.string().max(600).nullable().default(null),
});
export const conceptRegulatorySchema = z.object({
    jurisdiction: z.string().min(1).max(40),
    /** Pointer to the Jira REQ that carries the actual rule wording. */
    reqKey: z.string().max(60).nullable().default(null),
    rule: conceptRegulatoryRuleSchema,
});
export const conceptSchema = z.object({
    id: z.string().min(1),
    /** Stable machine slug used as the lookup key from tests / taggers, e.g. `buy_feature`. */
    slug: z.string().min(1).max(80),
    canonicalName: z.string().min(1).max(120),
    category: conceptCategorySchema,
    definition: z.string().max(4000).default(''),
    /** LOAD-BEARING functional invariant - "pay to skip straight to the bonus". */
    invariant: z.string().max(2000).default(''),
    aliases: z.array(conceptAliasSchema).default([]),
    signals: z.array(conceptSignalSchema).default([]),
    relations: z.array(conceptRelationSchema).default([]),
    examples: z.array(conceptExampleSchema).default([]),
    /** When true, this concept appears in the closed-vocab evidence tagger menu. */
    taggable: z.boolean().default(false),
    /** Bridge to the legacy evidence-label / Jira indicator key, e.g. `gp-gamble`. */
    jiraIndicatorKey: z.string().max(40).nullable().default(null),
    regulatoryRelevance: z.array(conceptRegulatorySchema).default([]),
    status: conceptStatusSchema.default('active'),
    updatedAt: z.string(),
});
/** Create / replace payload: everything except server-managed id + updatedAt. */
export const conceptDraftSchema = conceptSchema.omit({ id: true, updatedAt: true });
/** Partial patch payload for editing an existing concept. */
export const conceptPatchSchema = conceptDraftSchema.partial();
/**
 * Append-only audit row for the concept catalogue. `before` / `after` are
 * JSON snapshots (stringified Concept) so any historical state can be
 * reconstructed and diffed; `reason` is required on regulatory edits. The
 * revisions table is never UPDATE/DELETEd - it is the lab's audit trail.
 */
export const conceptRevisionSchema = z.object({
    id: z.string().min(1),
    conceptId: z.string().min(1),
    editor: z.string().max(120).default('system'),
    action: z.enum(['create', 'update', 'delete']),
    before: z.string().nullable().default(null),
    after: z.string().nullable().default(null),
    reason: z.string().max(1000).default(''),
    changedAt: z.string(),
});
/** Compact concept-index row (canonicalName + aliases) for cheap recognition injection. */
export const conceptIndexEntrySchema = z.object({
    id: z.string(),
    slug: z.string(),
    canonicalName: z.string(),
    category: conceptCategorySchema,
    aliases: z.array(z.string()).default([]),
});
// Tester-correction capture (eval loop) -------------------------------------
//
// When a tester overrides the AI verdict (tester_verdict != ai_verdict) we
// capture a structured row: the paired verdicts, a free-text explanation, and
// a `failureCategory` that diagnoses WHICH layer broke. Grouping by category
// tells us where to invest; only `recognition` misses loop back to the
// ontology. This is pure labelled data - no model training involved.
export const failureCategorySchema = z.enum([
    // Ontology did not map the mechanic -> fix = add alias / branding.
    'recognition',
    // Vision/video never captured the needed state -> fix = vision layer.
    'observation',
    // Saw everything, reasoned wrong -> fix = invariant / disambiguation.
    'judgment',
    // The requirement/test it was handed was itself wrong -> fix = guidance/Jira.
    'data',
]);
export const correctionSchema = z.object({
    id: z.string().min(1),
    runId: z.string(),
    testKey: z.string(),
    studio: z.string().default(''),
    game: z.string().default(''),
    aiVerdict: verdictParseSchema.nullable(),
    humanVerdict: verdictParseSchema.nullable(),
    explanation: z.string().default(''),
    failureCategory: failureCategorySchema.nullable().default(null),
    createdAt: z.string(),
});
/**
 * How a test should be routed. Most of the 500-600 lab tests do not need the
 * flagship aggregator: `measurement` is timestamp arithmetic (e.g. spin >=
 * 2.5s), `observation` is a vision persistence-check across frames (e.g.
 * clock visible at all times), `recognition` is ontology mapping + trivial
 * lookup, and `reasoning` is the full source-grounded aggregator.
 */
export const testTypeSchema = z.enum([
    'reasoning',
    'recognition',
    'observation',
    'measurement',
]);
/**
 * Per-TLIB guidance the tester wants the AI aggregator to take as
 * authoritative context for that specific test (evidence requirements,
 * PASSED / FAILED / N/A criteria, plus an optional N/A comment that gets
 * auto-appended to the AI's rationale when the verdict comes back as
 * N/A). Keyed by the Xray test key (e.g. "TLIB-20"). All body fields are
 * optional so a tester can fill in only what they want to clarify.
 */
export const testGuidanceSchema = z.object({
    id: z.string().min(1),
    testKey: z.string().min(1).max(60),
    /**
     * Free-form context the AI should read on top of what is already in the
     * Xray task (summary, description, steps). Use this for clarifications,
     * gotchas, "NB!" notes, regulatory background, etc. - NOT for evidence
     * requirements, which are already captured on the Xray side.
     */
    description: z.string().max(4000).default(''),
    passedCriteria: z.string().max(2000).default(''),
    failedCriteria: z.string().max(2000).default(''),
    naCriteria: z.string().max(2000).default(''),
    naComment: z.string().max(2000).default(''),
    /**
     * Concept slugs (ontology) this test is ABOUT. When set, the aggregator
     * injects only these concept cards instead of the whole vocabulary, so the
     * prompt stays small regardless of how big the catalogue grows.
     */
    conceptIds: z.array(z.string()).default([]),
    /**
     * Routing hint. `reasoning` (default) goes to the flagship aggregator;
     * `measurement` / `observation` are non-LLM deterministic routes;
     * `recognition` is ontology-mapping-heavy. See {@link testTypeSchema}.
     */
    testType: testTypeSchema.default('reasoning'),
    updatedAt: z.string().datetime(),
});
export const testGuidanceBundleSchema = z.object({
    version: z.literal(1),
    entries: z.array(testGuidanceSchema),
});
export const envLinkStatusSchema = z.enum(['ok', 'no-game-task', 'no-recipe', 'incomplete']);
export const envLinkRowSchema = z.object({
    jurisdiction: z.string(),
    /** Effective market actually used to look up the Game task (after alias expansion, e.g. GE -> UK). */
    resolvedMarket: z.string().nullable(),
    status: envLinkStatusSchema,
    gameTaskKey: z.string().nullable(),
    market: z.string().nullable(),
    gameId: z.string().nullable(),
    gameName: z.string().nullable(),
    playUrl: z.string().nullable(),
    generatedUrl: z.string().nullable(),
    guideMd: z.string().nullable(),
    warnings: z.array(z.string()).default([]),
});
export const envLinkBundleMetaSchema = z.object({
    fetchedAt: z.string(),
    stale: z.boolean(),
    staleReason: z.string().optional(),
    jiraHost: z.string().optional(),
});
export const envLinkBundleSchema = z.object({
    studio: z.string(),
    studioSlug: z.string(),
    gameName: z.string().nullable(),
    orderKey: z.string().nullable(),
    texecKey: z.string(),
    recipeStatus: z.enum(['ok', 'missing', 'invalid']),
    recipeError: z.string().nullable().default(null),
    jurisdictions: z.array(envLinkRowSchema),
    meta: envLinkBundleMetaSchema.optional(),
});
// Phase 2 - Preconditions ----------------------------------------------------
//
// The Testing Preconditions task (TP-XXXXX) lives in the same SR as the
// TEXEC and carries a Smart Checklist with ~30 items grouped by category.
// We parse it into structured items, let the tester stage local
// status/notes/auto-verify suggestions inside the app, and push the whole
// diff back to the Jira Smart Checklist as a single approved batch.
export const preconditionStatusSchema = z.enum(['todo', 'done', 'skipped']);
export const preconditionVerifyStatusSchema = z.enum([
    'todo',
    'done',
    'skipped',
    'manual',
]);
export const preconditionItemSchema = z.object({
    /** Stable hash of `group + label` so re-pulls can keep stage edits across edits in Jira. */
    id: z.string(),
    /** Smart Checklist header that contained this item; empty string when ungrouped. */
    group: z.string(),
    /** Item label (the line itself), stripped of `- [ ]`, `>STATUS` and surrounding whitespace. */
    label: z.string(),
    /** Indented body lines under the item, joined with newlines. */
    body: z.string().nullable().default(null),
    /** Authoritative status from Jira at last pull. */
    status: preconditionStatusSchema,
    /**
     * Original raw line from the Smart Checklist markdown (e.g. `~ Label`).
     * Preserved so items the user didn't stage round-trip verbatim and
     * non-default Smart Checklist statuses (in_progress, blocked, ...) are
     * not corrupted when we push.
     */
    rawLine: z.string().nullable().default(null),
    /**
     * Static guide prose for this item, sourced from the precondition
     * playbook PDF. Rendered as Markdown in the expanded row so testers
     * don't have to leave the app to know what to check. `null` when no
     * matching guide entry exists.
     */
    guide: z.string().nullable().default(null),
    /** Optional auto-verify suggestion + evidence. Cleared on every pull. */
    suggestedStatus: preconditionVerifyStatusSchema.nullable().default(null),
    suggestedEvidence: z.string().nullable().default(null),
});
export const preconditionStageEntrySchema = z.object({
    status: preconditionStatusSchema.optional(),
    note: z.string().nullable().optional(),
});
export const preconditionBundleMetaSchema = z.object({
    fetchedAt: z.string(),
    stale: z.boolean().default(false),
    staleReason: z.string().optional(),
    jiraHost: z.string().optional(),
    /** Custom-field id resolved on last pull (informational; for debugging). */
    fieldId: z.string().nullable().default(null),
});
export const preconditionBundleSchema = z.object({
    srKey: z.string(),
    tpKey: z.string().nullable(),
    tpUrl: z.string().nullable(),
    tpSummary: z.string().nullable(),
    /** Items as they currently exist in Jira (sorted in original order). */
    items: z.array(preconditionItemSchema),
    /** Locally staged overrides keyed by item id. */
    staged: z.record(z.string(), preconditionStageEntrySchema).default({}),
    pushedAt: z.string().nullable().default(null),
    meta: preconditionBundleMetaSchema.optional(),
});
export const preconditionPushResultSchema = z.object({
    pushed: z.number(),
    tpKey: z.string(),
    pushedAt: z.string(),
});
export const apiSuccessSchema = (data) => z.object({ success: z.literal(true), data });
export const apiErrorSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    code: z.string().optional(),
});
export const VERDICT_VALUES = verdictSchema.options;
// Jurisdiction match helper -------------------------------------------------
//
// Used by both the server (REQ filtering for AI prompts and bug drafts) and
// the web client (Related Requirements section). Lifting it here keeps the
// alias table in lock-step on both sides; previously it lived once in
// `apps/server/src/config/jurisdictionAliases.ts` and once in
// `apps/web/src/lib/jurisdictionMatch.ts`.
/**
 * Maps a TEXEC jurisdiction code to acceptable REQ jurisdiction codes. The
 * original code is always kept too. Seeded with `GE -> UK` because GE
 * markets reuse the UK regulatory body's requirements.
 */
export const JURISDICTION_ALIASES = {
    GE: ['UK'],
};
/**
 * Returns the set of REQ jurisdiction codes that should match a TEXEC tagged
 * with `codes`. Always includes the original codes (upper-cased) plus any
 * aliases configured above.
 */
export function expandJurisdictions(codes) {
    const out = new Set();
    for (const c of codes) {
        if (!c)
            continue;
        const upper = c.toUpperCase();
        out.add(upper);
        for (const alias of JURISDICTION_ALIASES[upper] ?? []) {
            out.add(alias.toUpperCase());
        }
    }
    return out;
}
/**
 * Returns the requirements that apply to the given TEXEC jurisdictions, with
 * GE -> UK aliasing applied. Two deliberate fallbacks:
 *   - When `texecCodes` is empty (TEXEC not yet loaded) we return everything,
 *     so the section is informative rather than blank during the brief gap
 *     before Phase 0 hydrates.
 *   - When a REQ has no jurisdictions tagged at all we keep it. These are
 *     usually older requirements that never had the field populated; hiding
 *     them silently is worse than showing them with a "no jurisdictions"
 *     hint in the UI.
 */
export function filterRequirementsForJurisdictions(reqs, texecCodes) {
    if (!reqs || reqs.length === 0)
        return [];
    if (!texecCodes || texecCodes.length === 0)
        return reqs;
    const allowed = expandJurisdictions(texecCodes);
    return reqs.filter((req) => {
        if (!req.jurisdictions || req.jurisdictions.length === 0)
            return true;
        return req.jurisdictions.some((j) => allowed.has(j.toUpperCase()));
    });
}
// ---------------------------------------------------------------------------
// Project-family helpers
//
// The Jira instance hosts two parallel project families:
//   - Live: SR (Service Request, the parent order) + TEXEC (Test Execution).
//   - Training/dev: XOT (training Service Request) + XET (training Test
//     Execution), plus XPOR (training client orders + Game tasks) and XPT
//     (training Testing-preconditions sub-tasks).
//
// XPOR and XPT don't appear here because the only code paths that touch
// them (`fetchLinkedGames` follows issuelinks, `discoverTp` joins by
// `parent = <srKey> + summary ~ "Testing preconditions"`) are already
// project-agnostic. Validation + filtering, however, has historically been
// hard-coded to SR / TEXEC; these constants are the single source of truth
// for both backend and frontend so we never get a regex out of sync.
// ---------------------------------------------------------------------------
export const PARENT_PROJECT_KEYS = ['SR', 'XOT'];
export const EXECUTION_PROJECT_KEYS = ['TEXEC', 'XET'];
/**
 * Anything in this list renders a "Training" badge in the UI. Includes the
 * client-orders project (XPOR) and the precondition sub-task project (XPT)
 * even though we don't filter on them server-side, so per-issue badges can
 * fire correctly if those keys ever surface in the UI.
 */
export const TRAINING_PROJECT_PREFIXES = ['XOT-', 'XET-', 'XPOR-', 'XPT-'];
/** True when the key looks like a parent order in either family. */
export function isParentKey(key) {
    const upper = key.toUpperCase();
    return PARENT_PROJECT_KEYS.some((p) => upper.startsWith(`${p}-`));
}
/** True when the key looks like a test-execution issue in either family. */
export function isExecutionKey(key) {
    const upper = key.toUpperCase();
    return EXECUTION_PROJECT_KEYS.some((p) => upper.startsWith(`${p}-`));
}
/** True when the key belongs to the X-prefixed training/dev family. */
export function isTrainingKey(key) {
    const upper = key.toUpperCase();
    return TRAINING_PROJECT_PREFIXES.some((p) => upper.startsWith(p));
}
// ---------------------------------------------------------------------------
// Stake Engine client projects
// ---------------------------------------------------------------------------
// EasyGo Games (Jira client-project key EAS) ships games as Stake Engine
// submissions rather than the usual studio source tree, so they get the
// dedicated Stake Engine verification mode. The match is by Client Project
// name (what we persist as `run.studio`, i.e. `customfield_10107.name`) or by
// the project key. Kept as a list so more Stake-engine clients can be added
// without touching call sites.
// ---------------------------------------------------------------------------
export const STAKE_ENGINE_CLIENT_PROJECTS = [
    { name: 'Easygo Games', key: 'EAS' },
];
/**
 * True when a run's Client Project (studio name or Jira key) is a Stake
 * Engine client. Case-insensitive; tolerates "EasyGo Games" / "Easygo".
 */
export function isStakeEngineStudio(studioOrKey) {
    if (!studioOrKey)
        return false;
    const v = studioOrKey.trim().toLowerCase();
    if (!v)
        return false;
    return STAKE_ENGINE_CLIENT_PROJECTS.some((c) => v === c.key.toLowerCase() || v.includes(c.name.toLowerCase()) || v.includes('easygo'));
}
//# sourceMappingURL=index.js.map